import React, { useEffect, useMemo, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import useSWR from 'swr'
import dayjs from 'dayjs'
import * as UIcons from '@iconscout/react-unicons'
import { withRouter } from 'hooks/withRouter'
import RewardAdsStats from './components/RewardAdsStats'
import RewardAdsFilterBar from './components/RewardAdsFilterBar'
import RewardAdsTable from './components/RewardAdsTable'
import RewardAdsPagination from './components/RewardAdsPagination'
import { normalizeStatus } from './utils/campaignStatus'

const CAMPAIGNS_KEY = 'promos/campaigns?page=1&page-size=1000'

const THUMBNAIL_TONES = ['primary', 'purple', 'success', 'gold']

function toRewardAd(campaign, index) {
  const daysLeft = campaign.end ? Math.max(0, dayjs(campaign.end).diff(dayjs(), 'day')) : 0

  return {
    id: campaign.id,
    title: campaign.title,
    status: normalizeStatus(campaign.status),
    thumbnailTone: THUMBNAIL_TONES[index % THUMBNAIL_TONES.length],
    period: {
      label: campaign.start && campaign.end
        ? `${dayjs(campaign.start).format('MMM D')} - ${dayjs(campaign.end).format('MMM D, YYYY')}`
        : '--',
      daysLeft
    },
    budget: campaign.budget ?? 0,
    dailyBudget: campaign.budget ?? 0,
    targeting: {
      audience: campaign.type ? `${campaign.type.charAt(0).toUpperCase()}${campaign.type.slice(1)} Campaign` : 'All Customers',
      age: campaign.schedules?.length ? `${campaign.schedules.length} schedule(s)` : '--'
    },
    frequency: {
      perUser: campaign.frequency?.name ?? '--',
      per: ''
    }
  }
}

// Reward ads listing is powered by the existing promo/campaign endpoint
// (`promos/campaigns`, the same one used on the /promo/campaign page) — there
// is no separate reward-ads-specific endpoint on the backend.
export default withTranslation()(withRouter(function RewardAds({ t, router: { navigate } }) {
  const { data, isValidating } = useSWR(CAMPAIGNS_KEY)

  const campaigns = useMemo(() => (data?.data.data ?? []).map(toRewardAd), [data])

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [targeting, setTargeting] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [selected, setSelected] = useState({})

  const filtered = useMemo(() => campaigns.filter((campaign) => {
    if (search && !campaign.title.toLowerCase().includes(search.toLowerCase())) return false
    if (status && campaign.status !== status) return false
    return true
  }), [campaigns, search, status])

  useEffect(() => { setPage(0) }, [search, status, targeting, pageSize])

  const paginated = filtered.slice(page * pageSize, (page + 1) * pageSize)
  const selectedCount = Object.values(selected).filter(Boolean).length

  const toggleSelect = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }))

  const goToCreate = () => navigate('/reward-ads/create')
  const goToDetail = (campaign) => navigate(`/reward-ads/${campaign.id}`)
  const notReady = () => toast.info('This action is coming soon')

  return (
    <section className="w-full max-w-6xl mx-auto pb-10">
      <div className="flex items-center justify-between gap-4 mb-5">
        <h1 className="text-2xl font-semibold text-dark-blue">{t('rewardAds.title')}</h1>

        <button
          type="button"
          onClick={goToCreate}
          className="flex items-center gap-1.5 bg-primary text-white rounded-full h-10 px-4 text-sm font-medium whitespace-nowrap"
        >
          <UIcons.UilPlus size={18} /> {t('rewardAds.create')}
        </button>
      </div>

      <RewardAdsStats campaigns={campaigns} />

      <div className="bg-white rounded-2xl border border-border-lines px-4 pt-4">
        <RewardAdsFilterBar
          search={search}
          onChangeSearch={setSearch}
          status={status}
          onChangeStatus={setStatus}
          targeting={targeting}
          onChangeTargeting={setTargeting}
        />

        {
          isValidating && !data
            ? <p className="text-sm text-secondary py-10 text-center">Loading reward ads...</p>
            : (
              <>
                <RewardAdsTable
                  campaigns={paginated}
                  hasAnyCampaigns={campaigns.length > 0}
                  selected={selected}
                  onToggleSelect={toggleSelect}
                  onCreate={goToCreate}
                  onViewDetails={goToDetail}
                  onEdit={notReady}
                  onDuplicate={notReady}
                  onViewAnalytics={goToDetail}
                  onEndCampaign={notReady}
                />

                <RewardAdsPagination
                  total={filtered.length}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                  selectedCount={selectedCount}
                />
              </>
            )
        }
      </div>
    </section>
  )
}))
